import type { GraphChangeSetWrite, GraphNode, GraphVisualizationFrame } from "@salesko/contracts";
import { stableTimelineReplayReason } from "./stableText";

// A single moment on the timeline. One moment === one event (1:1). The event is the
// protagonist/anchor; persons/organisations/opportunities/notes hang off it via edges.
export type TimelineMoment = {
  event: GraphNode; // type === "event"
  order: number; // 0-based position along the timeline
  occurredAt?: string; // event-only, ISO-like sortable value when known
  attached: GraphNode[]; // entities linked to this event (may be empty)
};

export type TimelineDisabledReason = "need_at_least_two_events";

export type DerivedTimeline = {
  enabled: boolean; // true once there are >= MIN_EVENTS events to lay along time
  reason?: TimelineDisabledReason; // stable code; UI/Miss Moments maps this to display copy
  warnings: string[]; // honest best-effort signals (e.g. a moment with no entities)
  moments: TimelineMoment[];
  orphans: GraphNode[]; // entities not attached to any event
};

const MIN_EVENTS = 2;
export const TIMELINE_GAP = 2.4;
export const TIMELINE_CAMERA_Y = 1.15;
export const TIMELINE_CAMERA_DIST = 6.4;
export const TIMELINE_RADIUS = 0.95;
export const TIMELINE_EVENT_SCALE = 1.7;

/**
 * Project a graph frame onto a timeline (events as protagonist).
 *
 * Pure function: it reads only `frame.graph.nodes` / `frame.graph.edges`. It never reads
 * the legacy `timeline[]`, never mutates input, and only treats an event's `occurredAt`
 * (ISO-like) as sortable time — relative phrases in `data.timeText` are display-only and
 * never used for ordering. Entity↔event links come straight from edges; an event with no
 * linked entity yields an empty `attached` plus a warning rather than a fabricated link.
 */
export function deriveTimeline(frame: GraphVisualizationFrame): DerivedTimeline {
  const nodes = frame.graph.nodes;
  const edges = frame.graph.edges;
  const events = nodes.filter((node) => node.type === "event");
  const warnings: string[] = [];

  const byId = new Map(nodes.map((node) => [node.id, node] as const));
  const appearance = new Map(nodes.map((node, index) => [node.id, index] as const));

  // Small, self-contained neighbor walk. Intentionally NOT reusing IntakeRoute's
  // neighborIdsFor (that file is mid-flight); extract a shared helper once it settles.
  const neighborIds = (nodeId: string): Set<string> => {
    const ids = new Set<string>();
    for (const edge of edges) {
      if (edge.source === nodeId) {
        ids.add(edge.target);
      }
      if (edge.target === nodeId) {
        ids.add(edge.source);
      }
    }
    return ids;
  };

  // Order by ISO-like occurredAt when present; dated events first, then by text appearance.
  const sortedEvents = [...events].sort((a, b) => {
    if (a.occurredAt && b.occurredAt) {
      if (a.occurredAt === b.occurredAt) {
        return (appearance.get(a.id) ?? 0) - (appearance.get(b.id) ?? 0);
      }
      return a.occurredAt < b.occurredAt ? -1 : 1;
    }
    if (a.occurredAt) {
      return -1;
    }
    if (b.occurredAt) {
      return 1;
    }
    return (appearance.get(a.id) ?? 0) - (appearance.get(b.id) ?? 0);
  });

  const attachedEntityIds = new Set<string>();
  const moments: TimelineMoment[] = sortedEvents.map((event, order) => {
    const attached: GraphNode[] = [];
    for (const id of neighborIds(event.id)) {
      const node = byId.get(id);
      if (node && node.type !== "event") {
        attached.push(node);
        attachedEntityIds.add(node.id);
      }
    }
    if (attached.length === 0) {
      warnings.push(`Moment "${event.label}" has no linked entities.`);
    }
    return {
      event,
      order,
      ...(event.occurredAt ? { occurredAt: event.occurredAt } : {}),
      attached
    };
  });

  const orphans = nodes.filter((node) => node.type !== "event" && !attachedEntityIds.has(node.id));

  const enabled = events.length >= MIN_EVENTS;
  return {
    enabled,
    ...(enabled ? {} : { reason: "need_at_least_two_events" as const }),
    warnings,
    moments,
    orphans
  };
}

export function timelineMomentZ(index: number, count: number) {
  return (index - (count - 1) / 2) * TIMELINE_GAP;
}

export function timelineReplayChangeSetWrite(
  frame: GraphVisualizationFrame,
  timeline: DerivedTimeline,
  focusIndex: number,
  zoomLevel = 1
): GraphChangeSetWrite | null {
  if (!timeline.enabled || timeline.moments.length === 0) {
    return null;
  }

  const nextFocusIndex = clampTimelineFocus(focusIndex, timeline.moments.length);
  const moment = timeline.moments[nextFocusIndex];
  if (!moment) {
    return null;
  }

  const safeZoomLevel = Math.max(zoomLevel, 0.1);
  const focusZ = timelineMomentZ(nextFocusIndex, timeline.moments.length);
  const selectedNodeId = moment.event.id;
  const camera: GraphVisualizationFrame["camera"] = {
    ...frame.camera,
    mode: "timeline",
    focusNodeId: selectedNodeId,
    position: {
      x: 0,
      y: TIMELINE_CAMERA_Y,
      z: focusZ + TIMELINE_CAMERA_DIST / safeZoomLevel
    },
    orbit: {
      ...frame.camera.orbit,
      autoRotate: false
    }
  };

  const activeView = "events" as const;
  if (
    frame.camera.mode === camera.mode &&
    frame.camera.focusNodeId === camera.focusNodeId &&
    frame.camera.position.x === camera.position.x &&
    frame.camera.position.y === camera.position.y &&
    frame.camera.position.z === camera.position.z &&
    frame.camera.orbit.autoRotate === camera.orbit.autoRotate &&
    frame.ui.detailPanel.selectedNodeId === selectedNodeId &&
    frame.ui.activeView === activeView
  ) {
    return null;
  }

  return {
    reason: stableTimelineReplayReason(moment.event.label),
    clientMutationId: `timeline-replay:${frame.frameId}:${stableTimelineReplayHash(
      `${selectedNodeId}\n${nextFocusIndex}\n${camera.position.z.toFixed(4)}`
    )}`.slice(0, 128),
    commands: [
      {
        type: "set_frame_view",
        activeView,
        camera,
        selectedNodeId
      }
    ]
  };
}

function clampTimelineFocus(index: number, count: number) {
  if (!Number.isFinite(index)) {
    return 0;
  }
  return Math.min(Math.max(Math.round(index), 0), Math.max(0, count - 1));
}

function stableTimelineReplayHash(value: string) {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }

  return (hash >>> 0).toString(36);
}
